import { useEffect, useRef, useState, useCallback, Component } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, ArrowLeft, Loader2, AlertTriangle, RotateCw, CheckCircle2, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * CourseVideoPlayer
 * Protected route: GET /courses/{id}/content returns 403 if user is not enrolled.
 * We surface that gracefully → redirect to /coaching.
 * Video player is wrapped in an ErrorBoundary + explicit retry.
 */
export default function CourseVideoPlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, course: null });
  const [activeId, setActiveId] = useState(null);
  const [completed, setCompleted] = useState({}); // { moduleId: true }
  const [videoState, setVideoState] = useState({ buffering: false, error: null });
  const [playerKey, setPlayerKey] = useState(0); // bump to force remount on retry
  const playerRef = useRef(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/courses/${courseId}/content`);
      setState({ loading: false, error: null, course: data });
      if (data.modules?.length) setActiveId((prev) => prev || data.modules[0].id);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        toast.info("You need to enroll in this course first.");
        navigate("/coaching", { replace: true });
        return;
      }
      setState({ loading: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, course: null });
    }
  }, [courseId, navigate]);

  useEffect(() => { load(); }, [load]);

  const activeModule = state.course?.modules?.find((m) => m.id === activeId);

  const retryVideo = () => {
    setVideoState({ buffering: false, error: null });
    setPlayerKey((k) => k + 1);
  };

  const chooseModule = (id) => {
    setActiveId(id);
    setVideoState({ buffering: false, error: null });
    setPlayerKey((k) => k + 1);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-white grid place-items-center">
        <Loader2 className="w-10 h-10 animate-spin text-brand-red" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-lg mx-auto py-24 px-6 text-center">
          <AlertTriangle className="w-12 h-12 text-brand-red mx-auto" />
          <div className="mt-4 font-heading font-black text-2xl text-brand-navy">Couldn&#39;t load this course</div>
          <div className="mt-2 text-brand-navy/60">{state.error}</div>
          <button onClick={load} data-testid="course-load-retry" className="mt-6 px-6 py-3 rounded-full bg-brand-red text-white font-semibold">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const course = state.course;
  const modules = course.modules || [];
  const activeIndex = modules.findIndex((m) => m.id === activeId);
  const progress = modules.length ? Math.round((Object.keys(completed).length / modules.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-brand-muted-bg">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button onClick={() => navigate("/coaching")} data-testid="video-back-btn" className="inline-flex items-center gap-1.5 text-brand-navy/60 hover:text-brand-red text-sm font-semibold mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Coaching
        </button>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3 order-2 lg:order-1">
            <div className="rounded-3xl bg-white border border-brand-navy/10 overflow-hidden">
              <div className="p-5 border-b border-brand-navy/10 bg-gradient-to-br from-brand-red to-brand-red-hover text-white">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">Enrolled</div>
                <div className="font-heading font-black text-xl mt-1">{course.title}</div>
                <div className="mt-3 h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div className="h-full bg-brand-gold transition-all" style={{ width: `${progress}%` }} data-testid="course-progress-bar" />
                </div>
                <div className="mt-2 text-xs text-white/80">{Object.keys(completed).length} / {modules.length} completed</div>
              </div>
              <ul className="divide-y divide-brand-navy/5" data-testid="module-list">
                {modules.map((m, i) => {
                  const isActive = m.id === activeId;
                  const isDone = !!completed[m.id];
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => chooseModule(m.id)}
                        data-testid={`module-btn-${m.id}`}
                        className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors ${isActive ? "bg-brand-red/5" : "hover:bg-brand-muted-bg"}`}
                      >
                        <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${
                          isDone ? "bg-emerald-500 text-white" : isActive ? "bg-brand-red text-white" : "bg-brand-navy/10 text-brand-navy"
                        }`}>
                          {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${isActive ? "text-brand-red" : "text-brand-navy"}`}>{m.title}</div>
                          <div className="text-[11px] text-brand-navy/50 mt-0.5">
                            {Math.floor((m.duration_sec || 0) / 60)}m {(m.duration_sec || 0) % 60}s
                          </div>
                        </div>
                        {!isActive && !isDone && <PlayCircle className="w-4 h-4 text-brand-navy/30 shrink-0 mt-1" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Player */}
          <main className="lg:col-span-8 xl:col-span-9 order-1 lg:order-2">
            <div className="rounded-3xl bg-black overflow-hidden relative aspect-video shadow-2xl">
              {activeModule ? (
                <VideoPane
                  key={playerKey}
                  playerRef={playerRef}
                  url={activeModule.video_url}
                  onBuffer={() => setVideoState((s) => ({ ...s, buffering: true }))}
                  onBufferEnd={() => setVideoState((s) => ({ ...s, buffering: false }))}
                  onReady={() => setVideoState({ buffering: false, error: null })}
                  onError={(e) => setVideoState({ buffering: false, error: e?.message || "Video failed to load" })}
                  onEnded={() => setCompleted((c) => ({ ...c, [activeModule.id]: true }))}
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-white/60">
                  <Lock className="w-8 h-8" />
                </div>
              )}

              {/* Loading overlay */}
              {activeModule && videoState.buffering && !videoState.error && (
                <div className="absolute inset-0 bg-black/60 grid place-items-center pointer-events-none" data-testid="video-loading">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-brand-gold mx-auto" />
                    <div className="mt-3 text-white/80 text-sm">Buffering…</div>
                  </div>
                </div>
              )}

              {/* Error overlay */}
              <AnimatePresence>
                {videoState.error && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-brand-navy/95 grid place-items-center px-6"
                    data-testid="video-error-overlay"
                  >
                    <div className="text-center max-w-sm">
                      <div className="w-14 h-14 rounded-full bg-brand-red/20 grid place-items-center mx-auto">
                        <AlertTriangle className="w-7 h-7 text-brand-red" />
                      </div>
                      <div className="mt-4 font-heading font-black text-xl text-white">Error loading video</div>
                      <div className="mt-2 text-white/60 text-sm">
                        Check your connection and click retry. The rest of your dashboard is still available.
                      </div>
                      <button
                        onClick={retryVideo}
                        data-testid="video-retry-btn"
                        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold btn-scale"
                      >
                        <RotateCw className="w-4 h-4" /> Click to retry
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Meta */}
            <div className="mt-6 rounded-3xl bg-white border border-brand-navy/10 p-6 md:p-7">
              <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">
                Module {activeIndex + 1} of {modules.length}
              </div>
              <h1 className="mt-2 font-heading font-black text-2xl md:text-3xl text-brand-navy" data-testid="active-module-title">
                {activeModule?.title || "Select a module"}
              </h1>
              <p className="mt-3 text-brand-navy/60 text-sm">
                Videos are hosted on Firebase Storage (or any HTTPS URL). Your progress is tracked locally in this session
                and syncs to your profile.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {activeIndex > 0 && (
                  <button
                    onClick={() => chooseModule(modules[activeIndex - 1].id)}
                    data-testid="module-prev-btn"
                    className="px-5 h-11 rounded-full border-2 border-brand-navy/15 text-brand-navy font-semibold hover:border-brand-navy btn-scale"
                  >
                    ← Previous
                  </button>
                )}
                {activeIndex < modules.length - 1 && (
                  <button
                    onClick={() => chooseModule(modules[activeIndex + 1].id)}
                    data-testid="module-next-btn"
                    className="px-5 h-11 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale"
                  >
                    Next module →
                  </button>
                )}
                {activeModule && !completed[activeModule.id] && (
                  <button
                    onClick={() => setCompleted((c) => ({ ...c, [activeModule.id]: true }))}
                    data-testid="module-mark-complete-btn"
                    className="px-5 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold btn-scale"
                  >
                    Mark complete
                  </button>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

// Isolated player pane so error boundary can wrap it cleanly.
class PlayerErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { this.props.onError?.(err); }
  render() { return this.state.hasError ? null : this.props.children; }
}

function VideoPane({ playerRef, url, onBuffer, onBufferEnd, onReady, onError, onEnded }) {
  // Ignore transient AbortError from React re-mounts / preload cancels.
  // Only surface an error after the browser explicitly reports a MediaError
  // AND a short grace period has elapsed (so we don't flash the error on mount).
  const errorTimerRef = useRef(null);
  useEffect(() => () => errorTimerRef.current && clearTimeout(errorTimerRef.current), []);

  const handleError = (e) => {
    const err = e?.currentTarget?.error;
    if (!err || !err.code) return;
    // If code is MEDIA_ERR_ABORTED (1) that's typically React remount noise — skip.
    if (err.code === 1) return;
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      const msgMap = { 2: "Network error", 3: "Decoding error", 4: "Format not supported" };
      onError({ message: msgMap[err.code] || "Video failed to load", code: err.code });
    }, 800);
  };
  const clearErrorTimer = () => {
    if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
  };

  return (
    <PlayerErrorBoundary onError={onError}>
      <video
        ref={playerRef}
        src={url}
        controls
        playsInline
        preload="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }}
        onWaiting={onBuffer}
        onPlaying={() => { clearErrorTimer(); onBufferEnd?.(); }}
        onCanPlay={() => { clearErrorTimer(); onBufferEnd?.(); onReady?.(); }}
        onLoadedData={() => { clearErrorTimer(); onReady?.(); }}
        onLoadedMetadata={clearErrorTimer}
        onError={handleError}
        onEnded={onEnded}
      />
    </PlayerErrorBoundary>
  );
}
